/**
 * Cart Recommendations
 * Loads complementary product recommendations in the cart drawer.
 * Uses polling to detect when the container appears (hydration/morphing bypass MutationObserver).
 * Listens for cart:update events to re-initialize after cart changes morph the container.
 *
 * "Lisää" button opens the quick-add modal so the user can pick the correct variant/size.
 */
(function() {
  'use strict';

  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + '\u20AC';
  }

  /**
   * Opens the quick-add modal for a product by fetching its page HTML,
   * extracting the product grid content, and showing the dialog.
   */
  function openQuickAdd(productUrl, button) {
    var originalText = button.innerHTML;
    button.innerHTML = '<span class="button-text">...</span>';
    button.disabled = true;

    fetch(productUrl)
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function(html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var productGrid = doc.querySelector('[data-product-grid-content]');
        var modalContent = document.getElementById('quick-add-modal-content');
        var dialog = document.getElementById('quick-add-dialog');

        if (!productGrid || !modalContent || !dialog) {
          // Fallback: navigate to product page if modal elements not found
          window.location.href = productUrl;
          return;
        }

        // Replace modal content (don't copy container attributes — the modal's
        // own quick-add-modal__content class provides the correct grid layout)
        modalContent.innerHTML = productGrid.innerHTML;

        // Open the dialog
        if (typeof dialog.showDialog === 'function') {
          dialog.showDialog();
        }

        button.innerHTML = originalText;
        button.disabled = false;
      })
      .catch(function() {
        // Fallback: navigate to product page on error
        window.location.href = productUrl;
      });
  }

  function hideContainer(container) {
    container.hidden = true;
    container.setAttribute('hidden', '');
    container.style.display = 'none';
  }

  function showContainer(container) {
    container.hidden = false;
    container.removeAttribute('hidden');
    container.style.display = '';
  }

  function escapeHtml(text) {
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function readManualProducts(container) {
    var el = container.querySelector('[data-cart-recs-manual]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function renderProducts(container, productsEl, products, limit) {
    // Button text comes from Teema-asetukset → Cart → Täydennä tilaustasi.
    var buttonLabel = escapeHtml(container.dataset.buttonLabel || 'Lis\u00E4\u00E4');

    var cartItemIds = new Set();
    document.querySelectorAll('[name="id"]').forEach(function(input) {
      if (input.closest('.cart-recommendations')) return;
      cartItemIds.add(input.value);
    });

    var seenUrls = new Set();
    var filtered = products.filter(function(p) {
      if (seenUrls.has(p.url)) return false;
      seenUrls.add(p.url);
      var variant = p.variants && p.variants[0];
      return variant && variant.available && !cartItemIds.has(String(variant.id));
    }).slice(0, limit);

    if (filtered.length === 0) return false;

    var html = filtered.map(function(product) {
      var image = product.featured_image
        ? '<div class="cart-recommendations__product-image"><img src="' + product.featured_image + '" alt="' + product.title.replace(/"/g, '&quot;') + '" loading="lazy" width="120" height="120"></div>'
        : '';

      var priceHtml = (product.compare_at_price && product.compare_at_price > product.price)
        ? '<s class="compare-at-price">' + formatMoney(product.compare_at_price) + '</s> <span class="sale-price">' + formatMoney(product.price) + '</span>'
        : formatMoney(product.price);

      var title = product.title.length > 40 ? product.title.substring(0, 37) + '...' : product.title;

      return '<div class="cart-recommendations__product" data-product-url="' + product.url + '">' +
        '<a href="' + product.url + '" class="cart-recommendations__product-link">' +
          image +
          '<div class="cart-recommendations__product-info">' +
            '<h4 class="cart-recommendations__product-title">' + title + '</h4>' +
            '<p class="cart-recommendations__product-price">' + priceHtml + '</p>' +
          '</div>' +
        '</a>' +
        '<button type="button" class="button button--small cart-recommendations__button" data-quick-add-url="' + product.url + '">' +
          '<span class="button-text">' + buttonLabel + '</span>' +
        '</button>' +
      '</div>';
    }).join('');

    productsEl.innerHTML = html;
    showContainer(container);

    productsEl.querySelectorAll('.cart-recommendations__button').forEach(function(button) {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        var productUrl = button.getAttribute('data-quick-add-url');
        if (productUrl) {
          openQuickAdd(productUrl, button);
        }
      });
    });

    return true;
  }

  function initRecommendations(container) {
    if (container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';

    var productsEl = container.querySelector('.cart-recommendations__products');
    var productId = container.dataset.productId;
    var baseUrl = container.dataset.url;
    var limit = parseInt(container.dataset.limit) || 3;

    if (!productId || !baseUrl || !productsEl) {
      hideContainer(container);
      return;
    }

    function fetchWithIntent(intent) {
      return fetch(baseUrl + '.json?product_id=' + productId + '&limit=' + limit + '&intent=' + intent)
        .then(function(response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        });
    }

    var source = container.dataset.source || 'auto';
    var manual = source === 'auto' ? [] : readManualProducts(container);

    if (source === 'manual') {
      if (!renderProducts(container, productsEl, manual, limit)) hideContainer(container);
      return;
    }

    // Try complementary first, fallback to related if empty/filtered out.
    // In "manual_then_auto" the selected products lead and the API fills the rest.
    fetchWithIntent('complementary')
      .then(function(data) {
        var rendered = renderProducts(container, productsEl, manual.concat(data.products || []), limit);
        var filledFromApi = rendered && (data.products || []).length > 0;
        if (filledFromApi) return null;
        return fetchWithIntent('related');
      })
      .then(function(data) {
        if (!data) return; // already rendered from complementary
        var rendered = renderProducts(container, productsEl, manual.concat(data.products || []), limit);
        if (!rendered) hideContainer(container);
      })
      .catch(function(e) {
        console.error('Cart recommendations error:', e);
        hideContainer(container);
      });
  }

  // Poll for the element — hydration and section rendering use morphing
  // which can bypass MutationObserver childList detection
  function checkForElement() {
    var el = document.querySelector('.cart-recommendations[data-product-id]');
    if (el && el.dataset.loaded !== 'true') {
      initRecommendations(el);
    }
  }

  // Check immediately, then poll every 500ms
  checkForElement();
  var pollInterval = setInterval(function() {
    checkForElement();
  }, 500);

  // Stop polling after 60 seconds to avoid unnecessary CPU usage
  setTimeout(function() {
    clearInterval(pollInterval);
  }, 60000);

  // Re-initialize after cart updates (morph resets the container HTML)
  document.addEventListener('cart:update', function() {
    // Wait for morphSection to complete before checking the element
    setTimeout(checkForElement, 100);
  });

  // Re-check when the cart drawer dialog opens — opening the drawer
  // triggers section hydration which resets data-loaded, but no cart:update
  // fires and the poll timer may have expired.
  var observer = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].attributeName === 'open' && mutations[i].target.hasAttribute('open')) {
        setTimeout(checkForElement, 200);
        break;
      }
    }
  });

  // Observe the cart drawer dialog (may not exist yet due to lazy hydration)
  function observeDrawer() {
    var dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog) {
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    } else {
      // Retry until the dialog element is hydrated
      setTimeout(observeDrawer, 1000);
    }
  }
  observeDrawer();
})();
